// frontend/components/events/EventPricing.tsx

'use client'

import React, { useState } from 'react';
import { Button, Spinner } from 'flowbite-react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { AiOutlineEdit, AiFillDelete } from 'react-icons/ai';
import { FaRegShareFromSquare } from 'react-icons/fa6';

import { Share } from '../share'; // Assuming this is your share component
import type { EventTicketPackage } from '../../models';

// --- Type Definitions for Props ---
interface Props {
    eventStatus?: string;
    // The specific list of packages to display (already filtered by the parent).
    packages: EventTicketPackage[];
    // A flag to determine if management controls (Add/Edit/Delete) should be shown.
    canManage?: boolean;
    // The base link for the event for generating share URLs.
    eventLink?: string;
    
    // Callbacks for all possible user actions.
    onAddPackage?: () => void;
    onEditPackage?: (pkg: EventTicketPackage) => void;
    onDeletePackage?: (packageId: string) => Promise<void> | undefined;
    onGetTicket?: (pkg: EventTicketPackage) => void;
}

const EventPricing: React.FC<Props> = ({
    eventStatus = 'upcoming', 
    packages = [],
    canManage = false,
    eventLink = '#',
    onAddPackage,
    onEditPackage,
    onDeletePackage,
    onGetTicket,
}) => {
    // State to track the ID of the package being deleted for per-card loading.
    const [deletingPackageId, setDeletingPackageId] = useState<string | null>(null);

    const handleDeleteClick = async (packageId: string) => {
        if (!onDeletePackage) return;
        setDeletingPackageId(packageId);
        try {
            await onDeletePackage(packageId);
        } catch (error) {
            console.error("Parent failed to delete package:", error);
        } finally {
            setDeletingPackageId(null);
        }
    };
    // console.log('test: ', eventStatus)
    return (
        <div className="bg-white pt-12">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-4xl text-center">
                    <h2 className="text-base font-semibold leading-7 text-indigo-600">Event Pricing</h2>
                    <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                        Pricing Packages
                    </p>
                </div>
                <p className="mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-gray-600">
                    Below are the different pricing packages for this event.
                </p>

                {canManage && onAddPackage && (
                    <div className="mt-10 flex items-center justify-center">
                        <Button onClick={onAddPackage}>
                            Add Package
                        </Button>
                    </div>
                )}
                
                {packages.length > 0 ? (
                    <div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                        {packages.map((pkg) => (
                            <div key={pkg.id} className="flex flex-col justify-between rounded-3xl bg-white p-8 ring-1 ring-gray-200">
                                <div>
                                    <div className="flex items-center justify-between gap-x-4">
                                        <h3 className="text-lg font-semibold leading-8 text-gray-900">{pkg.name}</h3>
                                        {pkg.code && (eventStatus === 'upcoming') && (
                                            <Share shareData={{ title: "Share Event Package", url: `${eventLink}&package_code=${pkg.code}` }}>
                                                {/* <button className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800"> */}
                                                    <FaRegShareFromSquare /> Share
                                                {/* </button> */}
                                            </Share>
                                        )}
                                    </div>
                                    <p className="mt-4 text-sm leading-6 text-gray-600">{pkg.description}</p>
                                    <div className="mt-6 flex gap-x-6">
                                        <p className="flex items-baseline gap-x-1"><span className="text-2xl font-bold">${pkg.priceMen}</span><span className="text-sm text-gray-600">/ men</span></p>
                                        <p className="flex items-baseline gap-x-1"><span className="text-2xl font-bold">${pkg.priceLadies}</span><span className="text-sm text-gray-600">/ ladies</span></p>
                                    </div>
                                    <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-gray-600">
                                        <li className="flex gap-x-3"><CheckIcon className="h-6 w-5 flex-none text-indigo-600" />Remaining: {pkg.availableTickets} / {pkg.tickets} tickets</li>
                                        <li className="flex gap-x-3"><CheckIcon className="h-6 w-5 flex-none text-indigo-600" />Max per user: {pkg.maxTicketsPerUser}</li>
                                        <li className="flex gap-x-3"><CheckIcon className="h-6 w-5 flex-none text-indigo-600" />Code: {pkg.code}</li>
                                        {canManage && <li className="flex gap-x-3"><CheckIcon className="h-6 w-5 flex-none text-indigo-600" />Sales: <b>${pkg.cash_balance ?? 0}</b></li>}
                                    </ul>
                                </div>

                                <div className="pt-8">
                                    {canManage ? 
                                        (
                                            <div className="flex divide-x divide-gray-200 rounded-b-lg border-t">
                                                {onEditPackage && <div className="flex w-0 flex-1"><button onClick={() => onEditPackage(pkg)} className="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900 hover:bg-gray-50"><AiOutlineEdit className="h-5 w-5 text-gray-400" />Edit</button></div>}
                                                {onDeletePackage && pkg.tickets === pkg.availableTickets && packages.length > 1 && <div className="-ml-px flex w-0 flex-1"><button onClick={() => handleDeleteClick(pkg.id)} disabled={deletingPackageId === pkg.id} className="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900 hover:bg-gray-50">{deletingPackageId === pkg.id ? <Spinner size="sm"/> : <><AiFillDelete className="h-5 w-5 text-gray-400" />Delete</>}</button></div>}
                                            </div>
                                        ) 
                                        : 
                                        eventStatus === 'upcoming'?
                                            (
                                                onGetTicket && (pkg.availableTickets > 0 ? (
                                                    <Button onClick={() => onGetTicket(pkg)} className="w-full">Get Ticket</Button>
                                                ) : (
                                                    <Button className="w-full" color="failure" disabled>Sold Out</Button>
                                                ))
                                            )
                                            :null
                                    }
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                     <p className="mt-10 text-center text-gray-500">No pricing packages are available for this event.</p>
                )}
            </div>
        </div>
    );
};

export default EventPricing;